import { CssBaseline } from '@mui/material';
import {
  ThemeProvider as MuiThemeProvider,
  createTheme as createMuiTheme,
} from '@mui/material/styles';
import React, { createContext, useContext, useMemo, useState } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';

import { createTheme, UnifiedTheme, ThemeMode } from './index';

interface ThemeContextValue {
  theme: UnifiedTheme;
  mode: ThemeMode;
  toggleMode: () => void;
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultMode?: ThemeMode;
  initialTheme?: UnifiedTheme;
}

export const UnifiedThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultMode = 'dark',
  initialTheme,
}) => {
  const [mode, setMode] = useState<ThemeMode>(defaultMode);

  const theme = useMemo(() => {
    return initialTheme || createTheme(mode);
  }, [mode, initialTheme]);

  // Create Material-UI theme based on our unified theme
  const muiTheme = useMemo(() => {
    return createMuiTheme({
      palette: {
        mode: theme.mode,
        primary: {
          main: theme.colors.primary,
        },
        secondary: {
          main: theme.colors.secondary,
        },
        background: {
          default: theme.colors.background,
          paper: theme.colors.cardBackground,
        },
        text: {
          primary: theme.colors.text,
          secondary: theme.colors.textSecondary,
        },
        error: {
          main: theme.colors.error,
        },
        warning: {
          main: theme.colors.warning,
        },
        info: {
          main: theme.colors.info,
        },
        success: {
          main: theme.colors.success,
        },
      },
      typography: {
        fontFamily: theme.typography.fonts.sans,
        h1: {
          fontFamily: theme.typography.fonts.mono,
          color: theme.colors.text,
        },
        h2: {
          fontFamily: theme.typography.fonts.mono,
          color: theme.colors.primary,
        },
        h3: {
          fontFamily: theme.typography.fonts.mono,
          color: theme.colors.primary,
        },
      },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: theme.borderRadius.sm,
              textTransform: 'none',
              fontWeight: theme.typography.fontWeights.medium,
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              backgroundColor: theme.colors.cardBackground,
              border: `1px solid ${theme.colors.cardBorder}`,
              borderRadius: theme.borderRadius.md,
            },
          },
        },
        MuiTextField: {
          styleOverrides: {
            root: {
              '& .MuiOutlinedInput-root': {
                backgroundColor: theme.colors.inputBg,
                '& fieldset': {
                  borderColor: theme.colors.inputBorder,
                },
                '&:hover fieldset': {
                  borderColor: theme.colors.borderBright,
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.colors.primary,
                },
              },
            },
          },
        },
      },
      shape: {
        borderRadius: parseInt(theme.borderRadius.md),
      },
    });
  }, [theme]);

  const toggleMode = () => {
    setMode(prev => (prev === 'dark' ? 'light' : 'dark'));
  };

  const contextValue: ThemeContextValue = {
    theme,
    mode,
    toggleMode,
    setMode,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <StyledThemeProvider theme={theme}>
        <MuiThemeProvider theme={muiTheme}>
          <CssBaseline />
          {children}
        </MuiThemeProvider>
      </StyledThemeProvider>
    </ThemeContext.Provider>
  );
};

// Export for backwards compatibility
export default UnifiedThemeProvider;
